import { Effect, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import * as Navigation from "foldkit/navigation";
import { evo } from "foldkit/struct";
import * as Update from "foldkit/update";

import { settle, start } from "../../api/asyncData";
import {
	fetchWeek,
	setCadence,
	skip,
	swap,
} from "../../api/commands/keepInTouch";
import { type ApiError, toMessage } from "../../api/errors";
import type { Cadence } from "../../api/schemas/records";
import { makeTrack } from "../../app/analytics";
import { i18next } from "../../i18n";
import * as Cards from "../../widgets/keepInTouchCards";
import * as Picker from "../../widgets/recordPicker";
import type { Page, PageReturn, Shared } from "../shared";
import { Message } from "./message";
import type { Model } from "./model";

// COMMANDS

/** KTD8: the week is now-only, so the fetch carries no asOf; generation still guards staleness. */
export const FetchWeek = Command.define("FetchWeek", {
	args: { generation: S.Number },
	messages: [Message.CompletedFetchWeek],
	execute: ({ generation }) =>
		fetchWeek.pipe(
			Effect.map((result) =>
				Message.CompletedFetchWeek({ generation, result }),
			),
		),
});
export const SkipCard = Command.define("SkipCard", {
	args: { recordId: S.String, generation: S.Number },
	messages: [Message.CompletedSkip],
	execute: ({ recordId, generation }) =>
		skip(recordId).pipe(
			Effect.map((result) =>
				Message.CompletedSkip({ recordId, generation, result }),
			),
		),
});
/** `replacementId` null asks the server for the next-ranked candidate (R12). */
export const SwapCard = Command.define("SwapCard", {
	args: {
		recordId: S.String,
		replacementId: S.NullOr(S.String),
		generation: S.Number,
	},
	messages: [Message.CompletedSwap],
	execute: ({ recordId, replacementId, generation }) =>
		swap(recordId, replacementId).pipe(
			Effect.map((result) =>
				Message.CompletedSwap({ recordId, replacementId, generation, result }),
			),
		),
});
export const SetCadence = setCadence.command(Message.CompletedSetCadence);
export const Track = makeTrack(Message.CompletedTrack);
export const Navigate = Command.define("NavigateFromKeepInTouch", {
	args: { url: S.String },
	messages: [Message.CompletedNavigate],
	execute: ({ url }) =>
		Navigation.pushUrl(url).pipe(Effect.as(Message.CompletedNavigate())),
});

export const initial: Model = {
	page: "KeepInTouch",
	week: AsyncData.Idle(),
	error: Option.none(),
	swapTarget: Option.none(),
	picker: Picker.init,
};

type Return = PageReturn<Model, Message>;

const load = (model: Model, shared: Shared): Return => ({
	model: evo(model, { week: start }),
	commands: [FetchWeek({ generation: shared.generation })],
});

const isConflict = (error: ApiError): boolean =>
	error._tag === "Http" && error.status === 409;

/** A write settled: success refetches; a 409 gets its translated message (skip refetches on it too, the card is already gone). */
const completedWrite = (
	model: Model,
	shared: Shared,
	generation: number,
	result: Result.Result<unknown, ApiError>,
	conflictKey: string | null,
	refetchOnConflict: boolean,
): Return => {
	if (generation !== shared.generation) return { model };
	return Result.match(result, {
		onSuccess: () => load(model, shared),
		onFailure: (error) => {
			const failed = evo(model, {
				error: () =>
					Option.some(
						isConflict(error) && conflictKey !== null
							? i18next.t(conflictKey)
							: toMessage(error),
					),
			});
			return isConflict(error) && refetchOnConflict
				? load(failed, shared)
				: { model: failed };
		},
	});
};

const clearError = (model: Model): Model =>
	evo(model, { error: () => Option.none() });

const cadenceWrite = (
	model: Model,
	shared: Shared,
	args: Readonly<{
		recordId: string;
		cadence: Cadence | null;
		paused: boolean;
	}>,
): Return => ({
	model: clearError(model),
	commands: [SetCadence({ ...args, generation: shared.generation })],
});

const foldPicker = (shared: Shared) =>
	Update.foldChild({
		update: (child: Picker.Model, message: Picker.Message) =>
			Picker.update(child, message, shared),
		read: (model: Model) => Option.some(model.picker),
		write: (model: Model, picker: Picker.Model) =>
			evo(model, { picker: () => picker }),
		toParentMessage: (message: Picker.Message) =>
			Message.GotPickerMessage({ message }),
	});

/** The card being replaced: the user's pick, else the first visible card. */
export const swapTargetOf = (model: Model): Option.Option<string> =>
	Option.orElse(model.swapTarget, () => {
		const week = AsyncData.getOrElse(model.week, () => null);
		return Option.fromNullishOr(
			week === null ? undefined : Cards.visible(week)[0]?.record.id,
		);
	});

export const page: Page<Model, Message> = {
	init: (_route, shared) => load(initial, shared),
	// The week ignores asOf (KTD8): nothing about the route changes what is fetched.
	changedRoute: (model) => ({ model }),
	update: (model, message, shared) =>
		Message.match<Return>(message, {
			CompletedFetchWeek: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: { model: evo(model, { week: (week) => settle(week, result) }) },
			GotCardMessage: ({ message }) =>
				Cards.Message.match<Return>(message, {
					ClickedSkip: ({ recordId }) => ({
						model: clearError(model),
						commands: [
							SkipCard({ recordId, generation: shared.generation }),
							Track({
								event: "keep_in_touch_card_action",
								props: { action: "skip" },
							}),
						],
					}),
					ClickedSwap: ({ recordId }) => ({
						model: clearError(model),
						commands: [
							SwapCard({
								recordId,
								replacementId: null,
								generation: shared.generation,
							}),
							Track({
								event: "keep_in_touch_card_action",
								props: { action: "swap" },
							}),
						],
					}),
					// KTD4: same destination as the card's record link.
					ClickedReachOut: ({ recordId }) => ({
						model,
						commands: [
							Navigate({ url: Cards.recordHref(shared.route, recordId) }),
						],
					}),
				}),
			CompletedSkip: ({ generation, result }) =>
				completedWrite(
					model,
					shared,
					generation,
					result,
					"keepInTouch.error.skipChanged",
					true,
				),
			CompletedSwap: ({ generation, result }) =>
				completedWrite(
					model,
					shared,
					generation,
					result,
					"keepInTouch.error.noSwap",
					false,
				),
			ChangedCadence: ({ recordId, cadence }) =>
				cadenceWrite(model, shared, { recordId, cadence, paused: false }),
			ClickedPause: ({ recordId, cadence }) =>
				cadenceWrite(model, shared, { recordId, cadence, paused: true }),
			ClickedUnpause: ({ recordId, cadence }) =>
				cadenceWrite(model, shared, { recordId, cadence, paused: false }),
			CompletedSetCadence: ({ generation, result }) =>
				completedWrite(model, shared, generation, result, null, false),
			ChangedSwapTarget: ({ recordId }) => ({
				model: evo(model, { swapTarget: () => Option.some(recordId) }),
			}),
			GotPickerMessage: ({ message }) => foldPicker(shared)(model, message),
			ClickedSwapIn: () =>
				Option.match(
					Option.all({
						chosen: model.picker.chosen,
						target: swapTargetOf(model),
					}),
					{
						onNone: () => ({ model }),
						onSome: ({ chosen, target }) => ({
							model: evo(clearError(model), {
								swapTarget: () => Option.none(),
								picker: () => Picker.init,
							}),
							commands: [
								SwapCard({
									recordId: target,
									replacementId: chosen.id,
									generation: shared.generation,
								}),
								Track({
									event: "keep_in_touch_card_action",
									props: { action: "swap-chosen" },
								}),
							],
						}),
					},
				),
			ClickedAddRecord: () => ({ model, commands: [Navigate({ url: "/" })] }),
			CompletedNavigate: () => ({ model }),
			CompletedTrack: () => ({ model }),
		}),
};
